import React, { useState, useEffect } from "react";
import {
  Card,
  Box,
  Flex,
  Text,
  Button,
  Badge,
  Indicator,
  ActionIcon,
  Modal,
} from "@mantine/core";
import { useNavigate } from "react-router-dom";
import { IconX } from "@tabler/icons-react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { AlertList } from "@components/Alerts";
import { getAlertEntityInfoFromContext } from "@components/Alerts/helpers";
import { DismissButton } from "@components/Alerts/AlertList";
import { useTranslation } from "react-i18next";

export default function AlertIndex() {
  const { t } = useTranslation();
  const [filter, setFilter] = useState("All");
  const location = useLocation();
  const activeContext = location.state?.context;
  const navigate = useNavigate();
  const adminInfo = useSelector((state) => state.admin);
  const managerInfo = useSelector((state) => state.manager);

  // React Query for fetching alerts
  const {
    data: stacks = [],
    isLoading: loading,
    isFetching,
  } = useQuery({
    queryKey: ["alertIndex", filter],
    queryFn: async () => {
      try {
        const response = await axios.post("/notifications/retrieve-stacks/", {
          organization_manager_id: managerInfo?.organization_manager_id,
          location_manager_id: managerInfo?.location_manager_id,
          admin_id: adminInfo?.admin_id,
        });
        const { data } = response;
        return data.response.map((m) => ({
          ...m,
          alerts: [
            {
              id: m.id,
              title: m.title,
              message_text: m.message_text,
              context: m.context,
            },
            // Add dummy alerts corresponding to context_count, up to a maximum of 3
            ...(m.context_count >= 3
              ? Array.from(
                  { length: Math.min(m.context_count - 1, 3) },
                  (_, index) => ({
                    id: `${m.id}-dummy-${index + 1}`,
                    title: `${m.title} (Additional ${index + 1})`,
                    message_text: `${
                      m.message_text
                    } - Additional notification ${index + 1}`,
                    context: m.context,
                  })
                )
              : []),
          ],
        }));
      } catch (error) {
        console.log(error);
        return false;
      }
    },
    staleTime: 0, // Always consider data stale to show loading on filter change
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });

  const handleAlertClick = (alert) => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    // Navigate to alerts page with the alert ID in state
    navigate("/notifications", {
      state: { context: alert.context },
    });
  };

  const activeContextItem = stacks.find(
    (item) => item.context === activeContext
  );

  if (!loading && !isFetching && stacks.length === 0) {
    return (
      <div>
        <Text fw={600} size="xl">
          {t("mixer:alerts:no_alerts_text")}
        </Text>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 800, margin: "0 auto" }}>
      {/* <Flex gap="xs" mb="md" align="center">
        <Flex gap="xs" align="center" style={{ flexGrow: 1 }}>
          <SegmentedControl
            data={["All", "Unread"]}
            value={filter}
            onChange={setFilter}
          />
          {(loading || isFetching) && <Loader size="xs" />}
        </Flex>
        <Button
          variant="subtle"
          size="xs"
          color="gray"
          onClick={handleMarkAllAsRead}
        >
          Mark all as read
        </Button>
      </Flex> */}
      {activeContextItem ? (
        <>
          <ContextDetail contextStack={activeContextItem} />
        </>
      ) : (
        <>
          {stacks.map((item, index) => (
            <Box key={item.context} mb={index <= stacks.length - 1 ? "lg" : 0}>
              <Flex align="center" gap="xs" mb="md">
                <Indicator
                  size={16}
                  label={item.context_count > 99 ? "99+" : item.context_count}
                  style={{
                    zIndex: 1,
                  }}
                  color={getAlertEntityInfoFromContext(item)?.color}
                >
                  <Badge
                    size="md"
                    color={getAlertEntityInfoFromContext(item)?.color}
                    variant="light"
                  >
                    {item.context_formatted}
                  </Badge>
                </Indicator>
              </Flex>
              <AlertList
                alerts={item.alerts}
                preview
                onAlertClick={handleAlertClick}
              />
            </Box>
          ))}
        </>
      )}
    </div>
  );
}

const ContextDetail = ({ contextStack }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const adminInfo = useSelector((state) => state.admin);
  const managerInfo = useSelector((state) => state.manager);
  const [alerts, setAlerts] = useState([]);
  const [filters, setFilters] = useState({
    search_value: "",
    read: false,
    unread: false,
    dismissed: false,
    page_size: 250,
    page: 0,
  });
  // the alert id from the widget, if one is present
  const widgetAlertId = location.state?.widgetAlertId;
  const [highlightedAlertId, setHighlightedAlertId] = useState(null);

  const {
    data,
    isLoading: loading,
    isFetching,
  } = useQuery({
    queryKey: ["contextAlerts", contextStack.context],
    queryFn: async () => {
      try {
        const response = await axios.post(
          "/notifications/retrieve-notifications/",
          {
            ...filters,
            organization_manager_id: managerInfo?.organization_manager_id,
            location_manager_id: managerInfo?.location_manager_id,
            admin_id: adminInfo?.admin_id,
            context: contextStack.context,
          }
        );
        const { data } = response;
        setAlerts(data.response[0].data);
        return data.response[0].data;
      } catch (error) {
        console.log(error);
        return false;
      }
    },
    staleTime: 0, // Always consider data stale to show loading on filter change
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });

  function handleDismiss(alertId) {
    setAlerts([...alerts].filter((alert) => alert.id !== alertId));
  }

  // Scroll to and highlight the widget alert when data loads
  useEffect(() => {
    if (widgetAlertId && alerts.length > 0 && !loading) {
      const matchingAlert = alerts.find((alert) => alert.id === widgetAlertId);
      if (matchingAlert) {
        // Find the alert element and scroll to it
        const alertElement = document.querySelector(
          `[data-alert-id="${widgetAlertId}"]`
        );
        if (alertElement) {
          // Scroll to the element
          alertElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });

          // Highlight the alert for 2 seconds
          setHighlightedAlertId(widgetAlertId);
          setTimeout(() => {
            setHighlightedAlertId(null);
          }, 2000);
        }
      }
    }
  }, [widgetAlertId, alerts, loading]);

  return (
    <div>
      <Flex align="center" gap="lg" mb="md">
        <Flex align="center" gap="lg" style={{ flexGrow: 1 }}>
          <Indicator
            size={16}
            label={alerts.length || contextStack.context_count}
            color={getAlertEntityInfoFromContext(contextStack)?.color}
            style={{
              zIndex: 1,
            }}
          >
            <Badge
              size="md"
              color={getAlertEntityInfoFromContext(contextStack)?.color}
              variant="light"
            >
              {contextStack.context_formatted}
            </Badge>
          </Indicator>
          <ActionIcon
            variant="light"
            radius="xl"
            size="xs"
            color="gray"
            onClick={() => {
              queryClient.invalidateQueries(["alertIndex"]);
              navigate("/notifications", { state: {} });
            }}
          >
            <IconX size={14} />
          </ActionIcon>
        </Flex>
        <DismissButton
          reqData={{
            organization_manager_id: managerInfo?.organization_manager_id,
            location_manager_id: managerInfo?.location_manager_id,
            admin_id: adminInfo?.admin_id,
            context: contextStack.context,
            notification_ids: alerts.map((alert) => alert.id),
            dismiss_all: true,
          }}
          count={contextStack.context_count}
          onSuccess={() => {
            queryClient.invalidateQueries(["alertIndex"]);
            navigate("/notifications", { state: {} });
          }}
        />
      </Flex>
      <Card p={0}>
        <AlertList
          // activeAlertId={alertId}
          alerts={alerts}
          expandable
          showDate
          onAlertClick={() => true}
          highlightedAlertId={highlightedAlertId}
          onDismiss={handleDismiss}
        />
      </Card>
    </div>
  );
};
